import $ from 'jquery';
import _ from 'underscore';
import { $body, isMobile } from 'util/global';
import * as urls from 'util/urls';
import { initGenericControllers } from 'app/generic_controllers';


/**
 * Add tooltip for birthday help button
 * @param {JQuery} $btn
 */
export function bdayExpl($btn) {
    let tooltipPlacement;
    if (isMobile) {
        tooltipPlacement = 'left';
    } else if ($btn.parents('.public-signup-modal').length) {
        tooltipPlacement = 'bottom';
    } else {
        tooltipPlacement = 'right';
    }

    const tooltipParams = {
        show: false,
        trigger: 'manual',
        placement: tooltipPlacement,
    };

    if (isMobile && $btn.hasClass('email-expl')) {
        // the only difference between this and default template is tooltip id
        tooltipParams.template =
            '<div class="tooltip" id="email-expl-tooltip">' +
                '<div class="tooltip-arrow"></div>' +
                '<div class="tooltip-inner"></div>' +
            '</div>';
    }

    $btn.tooltip(tooltipParams);

    const togglePopup = _.throttle(() => {
        $btn.tooltip('toggle');
        $btn.toggleClass('active');
    }, 200);

    $btn.on('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePopup();
    });

    $body.on('click', () => {
        if ($btn.hasClass('active')) {
            $btn.click();
        }
    });
}

/**
 * Controller for show/hide password button.
 * @param {JQuery} $btn
 */
export function showPassword($btn) {
    const fieldSelector = $btn.data('field');
    const $field = $btn.parentsUntil('form').parent().find(fieldSelector);
    let isActive = false;

    $btn.on('click', () => {
        isActive = !isActive;
        $btn.toggleClass('active', isActive);
        $field.attr('type', isActive ? 'text' : 'password');
    });
}

/**
 * Controller for signup/login modal
 * @param {JQuery} $container
 */
export function signUpModal($container) {
    // Load styles then forward to the controller
    const promises = [import('signup_modal.less')];
    if (isMobile) {
        promises.push(import('mobile/signup_modal.less'));
    } else {
        promises.push(import('desktop/signup_modal.less'));
    }
    // eslint-disable-next-line no-use-before-define
    Promise.all(promises).then(() => handleSignupModal($container));
}

/**
 * Controller for signup/login full page
 * @param {JQuery} $container
 */
export function signUpFullPage($container) {
    const $signupBtn = $container.find('#signup-email');
    const $loginBtn = $container.find('#problem-login-link');
    const $signupBtnAlt = $container.find('#problem-signup-link-alternative');
    const $loginBtnAlt = $container.find('#problem-login-link-alternative');
    const $inputs = $container.find('input');
    const formClass = 'show-form';
    const signupClass = 'show-signup';
    const loginClass = 'show-login';
    const focusedClass = 'focused';
    const VIEWS = {
        SIGNUP: 1,
        LOGIN: 2,
    };
    const signupTitle = 'Signup | Brilliant';
    const loginTitle = 'Login | Brilliant';

    // TODO one_step_signup_login_10_2021 -- If the experiment wins, one-step will be the default
    // way that this works, so we can remove the `show...Buttons` functions. If control wins,
    // just remove the variable and anything that uses it
    const isOneStep = $container.data('one-step') === true;

    const showSignupForm = () => {
        const focusInput = 'input#id_email';
        $container
            .removeClass(`${formClass} ${loginClass}`)
            .addClass(signupClass)
            .addClass(formClass);
        $container.find(focusInput).focus();
    };

    const showLoginForm = () => {
        const focusInput = 'input#id_login';
        $container
            .removeClass(`${formClass} ${signupClass}`)
            .addClass(loginClass)
            .addClass(formClass);
        $container.find(focusInput).trigger('focus');
    };

    const showSignupButtons = () => {
        if (isOneStep) {
            showSignupForm();
            return;
        }
        $container
            .removeClass(`${formClass} ${loginClass}`)
            .addClass(signupClass);
    };

    const showLoginButtons = () => {
        if (isOneStep) {
            showLoginForm();
            return;
        }
        $container
            .removeClass(`${formClass} ${signupClass}`)
            .addClass(loginClass);
    };

    $inputs.on('focus', (e) => {
        $(e.target).parents('.control-group').addClass(focusedClass);
    });

    $inputs.on('blur', (e) => {
        $(e.target).parents('.control-group').removeClass(focusedClass);
    });

    $(window).on('popstate', (e) => {
        const state = e.originalEvent ? e.originalEvent.state || {} : {};
        if (state.form === true) {
            if (state.view === VIEWS.SIGNUP) {
                showSignupForm();
            } else if (state.view === VIEWS.LOGIN) {
                showLoginForm();
            }
        } else if (state.view === VIEWS.SIGNUP) {
            showSignupButtons();
        } else if (state.view === VIEWS.LOGIN) {
            showLoginButtons();

        // no state
        } else if (window.location.href.indexOf('/signup/') >= 0) {
            showSignupButtons();
        } else {
            showLoginButtons();
        }
    });

    $signupBtn.on('click', (e) => {
        e.preventDefault();
        showSignupForm();
        const state = {
            form: true,
            view: VIEWS.SIGNUP,
        };
        const { next } = urls.getParamsObject();
        window.history.pushState(
            state,
            null,
            `${window.location.origin}/account/signup/?signup=true${next ? `&next=${next}` : ''}`,
        );
        document.title = signupTitle;
    });

    $loginBtn.on('click', (e) => {
        e.preventDefault();
        showLoginForm();
        const state = {
            form: true,
            view: VIEWS.LOGIN,
        };
        const { next } = urls.getParamsObject();
        window.history.pushState(
            state,
            loginTitle,
            `${window.location.origin}/account/login/?signup=true${next ? `&next=${next}` : ''}`,
        );
        document.title = loginTitle;
    });

    $signupBtnAlt.on('click', (e) => {
        e.preventDefault();
        showSignupButtons();
        const state = {
            form: false,
            view: VIEWS.SIGNUP,
        };
        const { next } = urls.getParamsObject();
        window.history.pushState(
            state,
            signupTitle,
            `${window.location.origin}/account/signup/${next ? `?next=${next}` : ''}`,
        );
        document.title = signupTitle;
    });

    $loginBtnAlt.on('click', (e) => {
        e.preventDefault();
        showLoginButtons();
        const state = {
            form: false,
            view: VIEWS.LOGIN,
        };
        const { next } = urls.getParamsObject();
        window.history.pushState(
            state,
            loginTitle,
            `${window.location.origin}/account/login/${next ? `?next=${next}` : ''}`,
        );
        document.title = loginTitle;
    });
}

function handleSignupModal($container) {
    // closes modal
    $container.parent('.modal-bg').on('click', () => {
        $container.parents('.solv-public-form').removeClass('open');
        $('.btn-mcq.dim').removeClass('dim');
    });

    $container.on('click', (e) => {
        e.stopPropagation();
    });

    const $formContainer = $container.find('#public-signup');
    const formClass = 'show-form';
    const isOneStep = $formContainer.data('one-step') === true;

    // loads signup and login forms
    const $signupBtn = $container.find('#signup-email');
    const $signupForm = $container.find('#signup-form-container');
    const $loginBtn = $container.find('#problem-login-link');
    const $loginForm = $container.find('#login-form-container');
    const signupBtnData = $signupBtn.data();
    const signupFormData = $signupForm.data();
    let signupFormLoaded = false;
    const loginBtnData = $loginBtn.data();
    const loginFormData = $loginForm.data();
    let loginFormLoaded = false;

    $signupBtn.on('click', (e) => {
        e.preventDefault();
        const focusInput = 'input#id_email';
        if (signupFormLoaded) {
            $formContainer.addClass(formClass);
            $container.addClass(formClass);
            $formContainer.find(focusInput).focus();
        } else {
            const data = _.extend({ page_key: signupFormData.pageKey }, signupBtnData);
            $.get(signupFormData.url, data)
                .then((response) => {
                    signupFormLoaded = true;
                    $signupForm.html(response.actions.ajax_signup_form.new_html);
                    initGenericControllers($signupForm);
                    $formContainer.addClass(formClass);
                    $container.addClass(formClass);
                    $formContainer.find(focusInput).focus();
                })
                .fail(() => {
                    window.location.href = $signupBtn.attr('href');
                });
        }
    });

    $loginBtn.on('click', (e) => {
        e.preventDefault();
        const focusInput = 'input#id_login';
        if (loginFormLoaded) {
            $formContainer.addClass(formClass);
            $container.addClass(formClass);
            $formContainer.find(focusInput).focus();
        } else {
            const data = _.extend({ page_key: loginFormData.pageKey }, loginBtnData);
            $.get(loginFormData.url, data)
                .then((response) => {
                    loginFormLoaded = true;
                    $loginForm.html(response.actions.ajax_login_form.new_html);
                    initGenericControllers($loginForm);
                    $formContainer.addClass(formClass);
                    $container.addClass(formClass);
                    $formContainer.find(focusInput).focus();
                })
                .fail(() => {
                    window.location.href = $loginBtn.attr('href');
                });
        }
    });

    // switches between signup and login
    const $signupBtnAlt = $container.find('#problem-signup-link-alternative');
    const $loginBtnAlt = $container.find('#problem-login-link-alternative');
    const signupClass = 'show-signup';
    const loginClass = 'show-login';

    $signupBtnAlt.on('click', (e) => {
        e.preventDefault();
        const removableClasses = [loginClass];
        if (isOneStep) {
            // You have to click the button to load the form :face_with_rolling_eyes:
            $signupBtn.trigger('click');
            if (!signupFormLoaded) {
                removableClasses.push(formClass);
            }
        } else {
            removableClasses.push(formClass);
        }
        $formContainer.removeClass(removableClasses.join(' ')).addClass(signupClass);
        $container.removeClass(formClass);
    });

    $loginBtnAlt.on('click', (e) => {
        e.preventDefault();
        const removableClasses = [signupClass];
        if (isOneStep) {
            // You have to click the button to load the form :face_with_rolling_eyes:
            $loginBtn.trigger('click');
            if (!loginFormLoaded) {
                removableClasses.push(formClass);
            }
        } else {
            removableClasses.push(formClass);
        }
        $formContainer.removeClass(removableClasses.join(' ')).addClass(loginClass);
        $container.removeClass(formClass);
    });
}

/**
 * Focus on the email input when the page loads
 * @param {JQuery} $form - reset password form
 */
export function resetPassword($form) {
    $form.find('input[type=email]').focus();
}

/**
 * Focus on the email input when the page loads
 * @param {JQuery} $form - verify email form
 */
export function verifyEmail($form) {
    $form.find('input[type=email]').focus();
}
